import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { X } from 'lucide-react';
import { cn } from '../../../utils';
import { cva, type VariantProps } from 'class-variance-authority';

export const ToastProvider = ToastPrimitive.Provider;

export const ToastViewport = forwardRef<
  ElementRef<typeof ToastPrimitive.Viewport>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>
>(({ className, ...props }, ref) => (
  // pointer-events-none on the viewport is the documented Radix Toast pattern:
  // the viewport spans up to 420×max-h-screen at bottom-right; without this,
  // its empty padding/flex area intercepts clicks on buttons underneath
  // while a toast is showing. Each Toast item re-enables clicks via
  // pointer-events-auto in toastVariants below.
  <ToastPrimitive.Viewport
    ref={ref}
    className={cn(
      'pointer-events-none fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4',
      'sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]',
      className
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitive.Viewport.displayName;

const toastVariants = cva(
  [
    'group pointer-events-auto relative flex w-full items-center justify-between',
    'space-x-4 overflow-hidden rounded-lg border p-4 pr-8 shadow-lg dark:shadow-slate-950/50 transition-all',
    'data-[swipe=cancel]:translate-x-0',
    'data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)]',
    'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
    'data-[swipe=move]:transition-none',
    'data-[state=open]:animate-in data-[state=closed]:animate-out',
    'data-[swipe=end]:animate-out',
    'data-[state=closed]:fade-out-80',
    'data-[state=closed]:slide-out-to-right-full',
    'data-[state=open]:slide-in-from-top-full',
    'data-[state=open]:sm:slide-in-from-bottom-full',
  ],
  {
    variants: {
      variant: {
        default: [
          'bg-slate-50 border-slate-200 text-slate-900',
          'dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100',
        ],
        success: [
          'bg-emerald-50 border-emerald-200 text-emerald-900',
          'dark:bg-emerald-950 dark:border-emerald-700 dark:text-emerald-100',
          '[&>svg]:text-emerald-600 dark:[&>svg]:text-emerald-400',
        ],
        error: [
          'bg-rose-50 border-rose-200 text-rose-900',
          'dark:bg-rose-950 dark:border-rose-700 dark:text-rose-100',
          '[&>svg]:text-rose-600 dark:[&>svg]:text-rose-400',
        ],
        warning: [
          'bg-amber-50 border-amber-200 text-amber-900',
          'dark:bg-amber-950 dark:border-amber-700 dark:text-amber-100',
          '[&>svg]:text-amber-600 dark:[&>svg]:text-amber-400',
        ],
        info: [
          'bg-sky-50 border-sky-200 text-sky-900',
          'dark:bg-sky-950 dark:border-sky-700 dark:text-sky-100',
          '[&>svg]:text-sky-600 dark:[&>svg]:text-sky-400',
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface ToastProps
  extends ComponentPropsWithoutRef<typeof ToastPrimitive.Root>,
    VariantProps<typeof toastVariants> {}

export const Toast = forwardRef<ElementRef<typeof ToastPrimitive.Root>, ToastProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <ToastPrimitive.Root
        ref={ref}
        className={cn(toastVariants({ variant }), className)}
        {...props}
      />
    );
  }
);
Toast.displayName = ToastPrimitive.Root.displayName;

export const ToastAction = forwardRef<
  ElementRef<typeof ToastPrimitive.Action>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Action
    ref={ref}
    className={cn(
      'inline-flex h-8 shrink-0 items-center justify-center rounded-md',
      'bg-slate-900 text-white px-3 text-sm font-medium',
      'transition-colors',
      'hover:bg-slate-800',
      'focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 dark:focus:ring-offset-slate-900',
      'disabled:pointer-events-none disabled:opacity-50',
      'dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100',
      'dark:focus:ring-slate-600',
      className
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitive.Action.displayName;

export const ToastClose = forwardRef<
  ElementRef<typeof ToastPrimitive.Close>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Close
    ref={ref}
    className={cn(
      'absolute right-2 top-2 rounded-md p-1 opacity-70',
      'transition-opacity',
      'hover:opacity-100',
      'focus:outline-none focus:ring-1 focus:ring-slate-300',
      'dark:focus:ring-slate-600',
      className
    )}
    toast-close=""
    aria-label="Close toast"
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitive.Close>
));
ToastClose.displayName = ToastPrimitive.Close.displayName;

export const ToastTitle = forwardRef<
  ElementRef<typeof ToastPrimitive.Title>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Title
    ref={ref}
    className={cn('text-sm font-semibold', className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitive.Title.displayName;

export const ToastDescription = forwardRef<
  ElementRef<typeof ToastPrimitive.Description>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Description
    ref={ref}
    className={cn('text-sm opacity-90', className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitive.Description.displayName;

export type ToastActionElement = React.ReactElement<typeof ToastAction>;
